import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { message } from 'antd';
import { appointmentService } from '../../../services/appointmentService';
import { useAuth } from '../../../contexts/AuthContext';
import type { Appointment } from '../../../types/api';
import ConfirmModal from '../../../components/common/ConfirmModal';
import Breadcrumb from '../../../components/ui/Breadcrumb';
import Spinner from '../../../components/ui/Spinner';
import { APPOINTMENT_STATUS_BADGE } from '../../../domain/statuses';
import { formatDate } from '../../../lib/format';

const STARTABLE_STATUSES = new Set(['SCHEDULED', 'CONFIRMED', 'WAITING']);

const REMINDER_BADGE: Record<string, { label: string; className: string }> = {
  pending: { label: 'Recordatorio: pendiente', className: 'custom-badge status-orange' },
  sent: { label: 'Recordatorio: enviado', className: 'custom-badge status-green' },
  failed: { label: 'Recordatorio: fallido', className: 'custom-badge status-red' },
  skipped: { label: 'Recordatorio: omitido', className: 'custom-badge status-grey' },
};

const AppointmentDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [appointment, setAppointment] = useState<Appointment | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [starting, setStarting] = useState(false);

  const loadAppointment = async (showSpinner = true) => {
    if (!id) return;
    if (showSpinner) setLoading(true);
    try {
      const data = await appointmentService.getById(Number(id));
      setAppointment(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching appointment:', err);
      setError('No se pudo cargar la cita');
    } finally {
      if (showSpinner) setLoading(false);
    }
  };

  useEffect(() => {
    void loadAppointment(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload when route id changes
  }, [id]);

  const requestDelete = () => {
    setShowDeleteConfirm(true);
  };

  const cancelDelete = () => {
    if (deleting) return;
    setShowDeleteConfirm(false);
  };

  const handleConfirmDelete = async () => {
    if (!id) return;
    setDeleting(true);
    try {
      await appointmentService.delete(Number(id));
      setShowDeleteConfirm(false);
      navigate('/appointments');
    } catch {
      setError('Error al eliminar la cita');
    } finally {
      setDeleting(false);
    }
  };

  const handleStartConsultation = async () => {
    if (!id) return;
    if (user?.doctor) {
      navigate(`/consultations/new?appointment_id=${id}`);
      return;
    }

    setStarting(true);
    try {
      await appointmentService.startConsultation(Number(id));
      message.success('Consulta iniciada');
      await loadAppointment(false);
    } catch {
      message.error('No se pudo iniciar la consulta');
    } finally {
      setStarting(false);
    }
  };

  if (loading) {
    return (
      <>
        <div className="page-header">
          <div className="row">
            <div className="col-sm-12">
              <Breadcrumb items={[{ label: 'Citas', to: '/appointments' }, { label: 'Detalle' }]} />
            </div>
          </div>
        </div>
        <Spinner variant="full-page" text="Cargando..." size="lg" />
      </>
    );
  }

  if (error || !appointment) {
    return (
      <>
        <div className="page-header">
          <div className="row">
            <div className="col-sm-12">
              <Breadcrumb items={[{ label: 'Citas', to: '/appointments' }, { label: 'Error' }]} />
            </div>
          </div>
        </div>
        <div className="alert alert-danger">{error || 'Cita no encontrada'}</div>
        <Link to="/appointments" className="btn btn-secondary">
          <i className="fa fa-arrow-left me-1"></i> Volver a Citas
        </Link>
      </>
    );
  }

  return (
    <>
      <div className="page-header">
        <div className="row align-items-center">
          <div className="col-sm-6">
            <Breadcrumb items={[{ label: 'Citas', to: '/appointments' }, { label: 'Detalle de Cita' }]} />
          </div>
          <div className="col-sm-6 text-end">
            {STARTABLE_STATUSES.has(appointment.status_code ?? '') && (
              <button
                className="btn btn-primary me-2"
                onClick={handleStartConsultation}
                disabled={starting}
              >
                <i className="fa fa-stethoscope me-1"></i> Iniciar Consulta
              </button>
            )}
            <button className="btn btn-danger" onClick={requestDelete}>
              <i className="fa fa-trash me-1"></i> Eliminar
            </button>
          </div>
        </div>
      </div>

      <div className="row">
        {/* Appointment Info */}
        <div className="col-md-8">
          <div className="card">
            <div className="card-body">
              <h4 className="card-title mb-3">
                <i className="fa fa-calendar me-2 text-primary"></i>
                Información de la Cita
              </h4>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="text-muted d-block">Fecha</label>
                  <strong>{formatDate(appointment.appointment_date)}</strong>
                </div>
                <div className="col-md-6 mb-3">
                  <label className="text-muted d-block">Hora</label>
                  <strong>{appointment.appointment_time || '-'}</strong>
                </div>
                <div className="col-md-6 mb-3">
                  <label className="text-muted d-block">Estado</label>
                  <span className={APPOINTMENT_STATUS_BADGE[appointment.status_code ?? ''] ?? 'custom-badge status-grey'}>
                    {appointment.status_name || '-'}
                  </span>
                  {appointment.reminder_status && REMINDER_BADGE[appointment.reminder_status] && (
                    <span className={`${REMINDER_BADGE[appointment.reminder_status].className} ms-2`}>
                      {REMINDER_BADGE[appointment.reminder_status].label}
                    </span>
                  )}
                </div>
                <div className="col-md-12 mb-3">
                  <label className="text-muted d-block">Motivo</label>
                  <strong>{appointment.reason || '-'}</strong>
                </div>
                {appointment.notes && (
                  <div className="col-md-12 mb-3">
                    <label className="text-muted d-block">Notas</label>
                    <p>{appointment.notes}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Patient & Doctor */}
        <div className="col-md-4">
          <div className="card mb-3">
            <div className="card-body">
              <h5 className="card-title">
                <i className="fa fa-user me-2 text-info"></i> Paciente
              </h5>
              {appointment.patient ? (
                <>
                  <p className="mb-1">
                    <strong>{appointment.patient.first_name} {appointment.patient.last_name}</strong>
                  </p>
                  {appointment.patient.identification && (
                    <small className="text-muted">{appointment.patient.identification}</small>
                  )}
                  {appointment.patient.whatsapp_phone && (
                    <>
                      <br />
                      <small className="text-muted">
                        <i className="fa fa-whatsapp me-1"></i>
                        {appointment.patient.whatsapp_phone}
                      </small>
                    </>
                  )}
                </>
              ) : (
                <p className="text-muted">Sin datos del paciente</p>
              )}
            </div>
          </div>

          <div className="card">
            <div className="card-body">
              <h5 className="card-title">
                <i className="fa fa-user-md me-2 text-success"></i> Doctor
              </h5>
              {appointment.doctor ? (
                <p className="mb-0">
                  <strong>Dr. {appointment.doctor.first_name} {appointment.doctor.last_name}</strong>
                </p>
              ) : (
                <p className="text-muted">Sin datos del doctor</p>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="mt-3">
        <Link to="/appointments" className="btn btn-secondary">
          <i className="fa fa-arrow-left me-1"></i> Volver a Citas
        </Link>
      </div>

      <ConfirmModal
        show={showDeleteConfirm}
        title="Eliminar cita"
        body="¿Está seguro de eliminar esta cita? Esta acción no se puede deshacer."
        confirmLabel="Eliminar"
        confirmVariant="danger"
        loading={deleting}
        onConfirm={handleConfirmDelete}
        onCancel={cancelDelete}
      />
    </>
  );
};

export default AppointmentDetailPage;
